import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { accountsApi, type AccountDto } from "@/api/endpoints";

interface Props {
  value: AccountDto | null;
  initialId?: string | null;
  onChange: (v: AccountDto | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 250) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/** Lookup over the chart of accounts (map a vendor to its payable account head — Code 11). */
export function AccountPicker({ value, initialId, onChange, label = "Account head", helperText, disabled }: Props) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);

  const q = useQuery({
    queryKey: ["accounts", "picker", debounced],
    queryFn: () => accountsApi.list({ search: debounced || undefined, isActive: true, pageSize: 50 }),
    staleTime: 60_000,
  });

  const resolveQuery = useQuery({
    queryKey: ["accounts", "resolve", initialId],
    queryFn: () => accountsApi.list({ pageSize: 500 }),
    enabled: !value && !!initialId,
    staleTime: 60_000,
  });
  const resolved = !value && initialId
    ? resolveQuery.data?.items.find(a => a.id === initialId) ?? null
    : value;

  return (
    <Autocomplete<AccountDto>
      value={resolved}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
